import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import axios from "./axios";

/**
 * Vehicle identity list — the source for every vehicle dropdown in the app.
 *
 * This exists because several screens used to ask the user to type a numeric
 * `vehicle_id`. A primary key is not something a fleet manager knows, and a
 * mistyped one does not fail loudly: it silently books a cost against the
 * wrong vehicle. A picker removes that class of error entirely, which is only
 * possible if there is one cheap, shared way to list vehicles.
 *
 * Deliberately NOT `useGetAllVehiculesFleet`: that endpoint carries eight
 * joins, the latest rent contract and mint statuses because the vehicle LIST
 * screen needs them. A dropdown needs a plate and a name.
 */

export interface VehicleOption {
  vehicle_id: number;
  plate: string | null;
  brand: string | null;
  model: string | null;
  vin: string | null;
  status: string | null;
}

export const vehicleOptionKeys = {
  list: (fleetId: number) => ["vehicle-options", fleetId] as const,
};

/** Matches the server-side cap in Fleet::getVehicleOptions. */
const OPTIONS_LIMIT = 1000;

/** Plate first: it is what appears on the receipt the user is looking at. */
export const vehicleOptionLabel = (option?: VehicleOption | null): string => {
  if (!option) return "";
  const name = [option.brand, option.model].filter(Boolean).join(" ").trim();
  if (option.plate && name) return `${option.plate} — ${name}`;
  return option.plate || name || `#${option.vehicle_id}`;
};

/** Everything a search should match, flattened once per option. */
export const vehicleOptionHaystack = (option: VehicleOption): string =>
  [option.plate, option.brand, option.model, option.vin, `#${option.vehicle_id}`]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

interface OptionsPayload {
  options: VehicleOption[];
  truncated: boolean;
}

const fetchVehicleOptions = async (fleetId: number): Promise<OptionsPayload> => {
  try {
    const response = await axios.get(`/fleets/${fleetId}/vehicle-options`, {
      params: { limit: OPTIONS_LIMIT },
    });

    return {
      options: response?.data?.data ?? [],
      truncated: !!response?.data?.truncated,
    };
  } catch (error: any) {
    // The API and this app deploy independently. Rather than leave every
    // picker dead until /vehicle-options ships, fall back to the list endpoint
    // and normalise its shape — heavier, but correct.
    if (error?.response?.status !== 404) throw error;

    const legacy = await axios.get(`/fleets/${fleetId}/vehicles`, {
      params: { page: 1, per_page: OPTIONS_LIMIT },
    });

    const rows: any[] = legacy?.data?.data ?? [];

    return {
      options: rows.map((row) => ({
        vehicle_id: row.vehicleId,
        plate: row.immatriculation ?? null,
        brand: row.brand ?? null,
        model: row.brandModel ?? null,
        vin: row.vin ?? null,
        status: row.fleetVehicleStatut ?? null,
      })),
      truncated: (legacy?.data?.total ?? 0) > rows.length,
    };
  }
};

export const useVehicleOptions = (fleetId: number) => {
  const { data, ...res } = useQuery({
    queryKey: vehicleOptionKeys.list(fleetId),
    enabled: typeof fleetId === "number" && !isNaN(fleetId) && fleetId > 0,
    // A fleet's roster changes on the order of days, and several pickers can
    // mount on one screen. Five minutes keeps them all on a single request.
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchVehicleOptions(fleetId),
  });

  const options = useMemo(() => data?.options ?? [], [data]);

  // Tables render ids they received from other endpoints; this is how they
  // resolve one to a plate without a request per row.
  const byId = useMemo(() => {
    const map = new Map<number, VehicleOption>();
    options.forEach((option) => map.set(option.vehicle_id, option));
    return map;
  }, [options]);

  return { options, byId, truncated: !!data?.truncated, ...res };
};

/** `#412` -> `AB-123-CD`, with a readable fallback when the id is unknown. */
export const usePlateResolver = (fleetId: number) => {
  const { byId } = useVehicleOptions(fleetId);

  return useMemo(
    () =>
      (vehicleId: number | null | undefined): string => {
        if (vehicleId === null || vehicleId === undefined) return "—";
        const option = byId.get(vehicleId);
        return option?.plate || vehicleOptionLabel(option) || `#${vehicleId}`;
      },
    [byId]
  );
};
